import { validateFieldValue } from "../../../utils/extensionFieldUtils";
import { ExtensionFieldCellEditor, CellEditorParams } from "./types";
import {
  CELL_EDITOR_STYLES,
  updateValidationState,
  handleCellEditorKeyboard,
} from "./utils/cellEditorStyles";

/**
 * Text Cell Editor for extension fields
 * Provides consistent styling and validation for text input in AG Grid inline editing
 */
export class TextCellEditor implements ExtensionFieldCellEditor {
  private eGui: HTMLElement;
  private params!: CellEditorParams;
  private inputElement: HTMLInputElement;
  private currentValue: string;
  private initialValue: string;
  private isDestroyed: boolean = false;

  // Bind event handlers to maintain proper 'this' context
  private boundHandleInputChange: (event: Event) => void;
  private boundHandleKeyDown: (event: KeyboardEvent) => void;
  private boundHandleBlur: () => void;

  constructor() {
    this.eGui = document.createElement("div");
    Object.assign(this.eGui.style, CELL_EDITOR_STYLES.container);
    this.currentValue = "";
    this.initialValue = "";

    // Bind event handlers once to avoid memory leaks
    this.boundHandleInputChange = this.handleInputChange.bind(this);
    this.boundHandleKeyDown = this.handleKeyDown.bind(this);
    this.boundHandleBlur = this.handleBlur.bind(this);

    this.inputElement = document.createElement("input");
    this.setupInputElement();
  }

  private setupInputElement(): void {
    this.inputElement.name = "cellValue";
    this.inputElement.placeholder = "Enter text...";

    // Apply consistent styling using shared constants
    Object.assign(this.inputElement.style, {
      ...CELL_EDITOR_STYLES.input,
      textAlign: "left", // Override right alignment for text inputs
    });

    // Add event listeners using bound handlers
    this.inputElement.addEventListener("input", this.boundHandleInputChange);
    this.inputElement.addEventListener("keydown", this.boundHandleKeyDown);
    this.inputElement.addEventListener("blur", this.boundHandleBlur);

    this.eGui.appendChild(this.inputElement);
  }

  private handleInputChange(event: Event): void {
    if (this.isDestroyed) return;

    const target = event.target as HTMLInputElement;
    const newValue = target.value;
    this.currentValue = newValue;

    // Validate the new value and update styling
    const validationError = validateFieldValue(newValue, this.params.fieldType);
    updateValidationState(this.inputElement, validationError);
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (this.isDestroyed) return;

    // Use shared keyboard handler for Enter/Escape behavior
    handleCellEditorKeyboard(event, {
      inputElement: this.inputElement,
      initialValue: this.initialValue,
      currentValueSetter: (value) => {
        this.currentValue = value;
      },
      api: this.params.api,
    });
  }

  private handleBlur(): void {
    if (this.isDestroyed) return;
    this.params.api.stopEditing();
  }

  init(params: CellEditorParams): void {
    this.params = params;

    // Extract initial value from the extension field value object
    const extensionFieldValue = params.value;

    this.initialValue = extensionFieldValue?.stringValue || "";
    this.currentValue = this.initialValue;

    // Set the input value
    this.inputElement.value = this.currentValue;
  }

  afterGuiAttached(): void {
    // Focus and select the input after the GUI is attached
    if (!this.isDestroyed && this.inputElement) {
      this.inputElement.focus();
      this.inputElement.select();
    }
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  getValue(): string {
    // Ensure we return the current input value
    if (this.inputElement) {
      this.currentValue = this.inputElement.value;
    }

    return this.currentValue;
  }

  destroy(): void {
    this.isDestroyed = true;

    // Remove event listeners to prevent memory leaks
    if (this.inputElement) {
      this.inputElement.removeEventListener("input", this.boundHandleInputChange);
      this.inputElement.removeEventListener("keydown", this.boundHandleKeyDown);
      this.inputElement.removeEventListener("blur", this.boundHandleBlur);
    }
  }

  isPopup(): boolean {
    return false;
  }

  isCancelBeforeStart(): boolean {
    return false;
  }

  isCancelAfterEnd(): boolean {
    return false;
  }

  focusIn(): void {
    if (!this.isDestroyed && this.inputElement) {
      this.inputElement.focus();
    }
  }

  focusOut(): void {
    // No specific action needed on focus out
  }
}
